import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Layers, Search, X, Wallet, WalletCards, TrendingUp, History, Landmark, HelpCircle } from "lucide-react";
import { EventImage } from "@/components/EventImage";
import { cn, formatBRL } from "@/lib/utils";
import { format } from "date-fns";
import { useSidebar } from "@/components/ui/sidebar";

export interface FinancialSidebarBalanceItem {
  event_id: string;
  event_title: string;
  event_end: string | null;
  start_date?: string | null;
  cover_image_url?: string | null;
  available: number;
  pix_total?: number;
  pix_released?: number;
  pix_pending?: number;
  credit_card_total?: number;
  card_total?: number;
  boleto_total?: number;
  post_event_released?: boolean;
  post_event_release?: string | null;
}

interface ProducerFinancialEventSidebarProps {
  balances: FinancialSidebarBalanceItem[];
  selectedEventId: string;
  onSelectEvent: (eventId: string) => void;
  totalAvailable: number;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onOpenRulesModal?: () => void;
}

export function ProducerFinancialEventSidebar({
  balances = [],
  selectedEventId,
  onSelectEvent,
  totalAvailable,
  activeTab = "saques",
  onTabChange,
  onOpenRulesModal,
}: ProducerFinancialEventSidebarProps) {
  const [search, setSearch] = useState("");
  const { isMobile, setOpenMobile } = useSidebar();

  const eventsWithBalanceCount = useMemo(
    () => balances.filter((b) => Number(b.available) > 0).length,
    [balances]
  );

  const [filterMode, setFilterMode] = useState<"all" | "with-balance">(
    eventsWithBalanceCount > 0 ? "with-balance" : "all"
  );

  const filteredBalances = useMemo(() => {
    let list = [...balances];
    if (filterMode === "with-balance") {
      list = list.filter((b) => Number(b.available || 0) > 0);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((b) => b.event_title.toLowerCase().includes(q));
    }
    // Ordena: maior saldo disponível primeiro, depois alfabético
    return list.sort((a, b) => {
      const balA = Number(a.available || 0);
      const balB = Number(b.available || 0);
      if (balA > 0 && balB <= 0) return -1;
      if (balB > 0 && balA <= 0) return 1;
      if (balA > 0 && balB > 0) return balB - balA;
      return (a.event_title || "").localeCompare(b.event_title || "");
    });
  }, [balances, search, filterMode]);

  const handleSelect = (id: string) => {
    onSelectEvent(id);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const navTabs = [
    { key: "saques", label: "Saques", icon: WalletCards },
    { key: "previsao", label: "Previsão", icon: TrendingUp },
    { key: "historico", label: "Histórico", icon: History },
    { key: "bancos", label: "Bancos", icon: Landmark },
  ];

  return (
    <div className="flex h-full flex-col gap-2 py-1 text-xs">
      {/* 1. Header: Retorno ao Menu Principal & Título */}
      <div className="px-2 pt-1 pb-2 space-y-2 border-b border-border/40">
        <div className="flex items-center justify-between">
          <Link
            to="/produtor/dashboard"
            onClick={() => {
              if (isMobile) setOpenMobile(false);
            }}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 px-2 py-1 rounded-xl transition-colors active:scale-[0.98]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Menu Principal</span>
          </Link>
          <span className="text-[10px] text-muted-foreground bg-secondary/70 border border-border/50 px-2 py-0.5 rounded-full font-medium">
            {balances.length} {balances.length === 1 ? "evento" : "eventos"}
          </span>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-foreground tracking-tight">Saques & Liberações</h2>
          <p className="text-[11px] text-muted-foreground">Filtre por evento para auditar valores</p>
        </div>

        {/* Sub-abas financeiras integradas na sidebar */}
        {onTabChange && (
          <div className="grid grid-cols-4 gap-1 p-1 rounded-2xl bg-secondary/50 border border-border/40 mt-1">
            {navTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    onTabChange(tab.key);
                    if (isMobile) setOpenMobile(false);
                  }}
                  className={cn(
                    "flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-xl text-[10px] font-medium transition-all",
                    isActive
                      ? "bg-background text-foreground font-semibold shadow-2xs"
                      : "text-muted-foreground hover:text-foreground hover:bg-background/40"
                  )}
                  title={tab.label}
                >
                  <Icon className={cn("h-3.5 w-3.5", isActive ? "text-primary" : "text-muted-foreground")} />
                  <span className="truncate max-w-full">{tab.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Campo de Busca Rápida */}
        <div className="relative pt-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar evento..."
            className="w-full rounded-xl border border-border/40 bg-secondary/40 pl-8 pr-7 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-border/80"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Segmented Filter: Com Saldo vs Todos */}
        <div className="flex items-center p-0.5 rounded-xl bg-secondary/50 border border-border/40 gap-1">
          <button
            type="button"
            onClick={() => setFilterMode("with-balance")}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-1 rounded-lg text-[10px] font-medium transition-all",
              filterMode === "with-balance"
                ? "bg-background text-foreground font-semibold shadow-2xs"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Wallet className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            <span>Com saldo ({eventsWithBalanceCount})</span>
          </button>
          <button
            type="button"
            onClick={() => setFilterMode("all")}
            className={cn(
              "flex-1 flex items-center justify-center py-1 rounded-lg text-[10px] font-medium transition-all",
              filterMode === "all"
                ? "bg-background text-foreground font-semibold shadow-2xs"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <span>Todos ({balances.length})</span>
          </button>
        </div>
      </div>

      {/* 2. Item "Visão Consolidada (Todos os Eventos)" */}
      {filterMode === "all" && (
        <div className="px-2 pt-0.5">
          <button
            type="button"
            onClick={() => handleSelect("all")}
            className={cn(
              "w-full text-left rounded-2xl p-2.5 transition-all flex items-center justify-between gap-2.5 border active:scale-[0.99]",
              selectedEventId === "all"
                ? "bg-secondary/90 border-border/80 shadow-2xs text-foreground font-semibold ring-1 ring-border/50"
                : "border-border/30 bg-background/50 hover:bg-secondary/40 text-muted-foreground hover:text-foreground"
            )}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all",
                selectedEventId === "all"
                  ? "border-primary bg-primary text-white"
                  : "border-border/70 bg-transparent"
              )}>
                {selectedEventId === "all" && <Check className="h-2.5 w-2.5 stroke-[3]" />}
              </div>

              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-secondary/80 border border-border/40 text-foreground">
                <Layers className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-semibold truncate text-foreground">Todos os eventos</p>
                <p className="text-[10px] text-muted-foreground truncate">Visão consolidada</p>
              </div>
            </div>

            <div className="text-right shrink-0">
              <span className="font-display text-xs font-semibold tabular-nums text-foreground block">
                {formatBRL(totalAvailable)}
              </span>
              <span className="text-[9px] text-muted-foreground uppercase">Disponível</span>
            </div>
          </button>
        </div>
      )}

      {/* 3. Lista de Eventos Disponíveis */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 space-y-1 custom-scrollbar pt-1">
        <div className="px-1 pb-1 flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
          <span>Eventos</span>
          <span>Disponível</span>
        </div>

        {filteredBalances.length === 0 ? (
          <div className="py-6 text-center px-3">
            <p className="text-xs font-medium text-foreground">Nenhum evento encontrado</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              {search
                ? `Sem resultados para "${search}"`
                : "Nenhum evento com saldo para saque neste momento."}
            </p>
            {(search || filterMode !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFilterMode("all");
                }}
                className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-[11px] font-medium text-foreground hover:bg-secondary/80 transition-colors"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          filteredBalances.map((b) => {
            const isSelected = selectedEventId === b.event_id;
            const cardBoleto = Number(b.credit_card_total ?? b.card_total ?? 0) + Number(b.boleto_total || 0);
            const hasBalance = Number(b.available || 0) > 0;

            return (
              <button
                key={b.event_id}
                type="button"
                onClick={() => handleSelect(isSelected ? "all" : b.event_id)}
                className={cn(
                  "w-full text-left rounded-2xl p-2 transition-all flex items-center justify-between gap-2.5 border group active:scale-[0.99]",
                  isSelected
                    ? "bg-secondary/90 border-border/80 shadow-2xs text-foreground font-medium ring-1 ring-border/50"
                    : "border-transparent hover:bg-secondary/40 text-foreground/85 hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all",
                    isSelected
                      ? "border-primary bg-primary text-white"
                      : "border-border/60 bg-transparent opacity-0 group-hover:opacity-60"
                  )}>
                    {isSelected && <Check className="h-2.5 w-2.5 stroke-[3]" />}
                  </div>

                  <div className="relative h-8 w-8 shrink-0 rounded-xl overflow-hidden border border-border/40 bg-muted">
                    <EventImage
                      src={b.cover_image_url}
                      alt={b.event_title}
                      className="h-full w-full object-cover"
                    />
                    {hasBalance && (
                      <span className="absolute bottom-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500 ring-1 ring-card" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground truncate max-w-[130px]" title={b.event_title}>
                      {b.event_title}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {b.event_end
                        ? `Fim: ${format(new Date(b.event_end), "dd/MM/yy")}`
                        : "Sem término"}
                    </p>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <span
                    className={cn(
                      "font-display text-xs tabular-nums block font-semibold",
                      hasBalance
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-muted-foreground font-normal"
                    )}
                  >
                    {formatBRL(b.available)}
                  </span>
                  {!b.post_event_released && cardBoleto > 0 ? (
                    <span className="text-[9px] text-amber-600 dark:text-amber-400 block tabular-nums">
                      +Retido: {formatBRL(cardBoleto)}
                    </span>
                  ) : (
                    <span className="text-[9px] text-muted-foreground block">
                      {b.post_event_released ? "Liberado" : "Disponível"}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* 4. Footer: Regras de Liberação */}
      {onOpenRulesModal && (
        <div className="p-2 border-t border-border/40 shrink-0">
          <button
            type="button"
            onClick={onOpenRulesModal}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl border border-border/40 bg-secondary/30 hover:bg-secondary/70 text-muted-foreground hover:text-foreground text-[11px] font-medium transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <HelpCircle className="h-3.5 w-3.5 text-primary" />
              <span>Regras de liberação</span>
            </span>
            <span className="text-[10px] text-muted-foreground/70">PIX D+2 & Cartão</span>
          </button>
        </div>
      )}
    </div>
  );
}